import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, FileText, BookOpen } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PdfViewer from "@/components/documents/PdfViewer";
import { API, fileUrl, formatBytes } from "@/lib/adminApi";

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("Toutes");
  const [open, setOpen] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/documents`).then((r) => setDocs(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const cats = [...new Set(docs.map((d) => d.category))];
  const shown = cat === "Toutes" ? docs : docs.filter((d) => d.category === cat);

  return (
    <div data-testid="documents-page" className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-[#FAF8F5]/90 px-4 py-4 backdrop-blur">
        <a data-testid="documents-back" href="/" className="mx-auto flex max-w-5xl items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-stone-500 transition-colors hover:text-[#C5A059]">
          <ArrowLeft className="h-4 w-4" /> Retour au mémorial
        </a>
      </header>

      <section className="px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <Reveal className="mb-10 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C5A059]">Archives</p>
            <h1 className="mt-3 font-display text-4xl font-semibold text-stone-900 sm:text-5xl">Documents</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500 sm:text-base">
              Biographie, hommages, programmes : à lire en ligne ou à télécharger.
            </p>
            {cats.length > 1 && (
              <div data-testid="documents-categories" className="mt-6 flex flex-wrap justify-center gap-2">
                {["Toutes", ...cats].map((c) => (
                  <button key={c} data-testid={`documents-category-${c}`} onClick={() => setCat(c)} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${cat === c ? "bg-[#1C1917] text-[#FAF8F5]" : "border border-stone-300 text-stone-500 hover:border-[#C5A059]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </Reveal>

          {loaded && !docs.length && (
            <p data-testid="documents-empty" className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-stone-300 p-10 text-sm text-stone-400">
              <BookOpen className="h-4 w-4" /> Aucun document pour le moment.
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {shown.map((d, i) => (
              <Reveal key={d.id} delay={(i % 2) * 0.08}>
                <button data-testid={`document-card-${d.id}`} onClick={() => setOpen(d)} className="group flex w-full items-start gap-4 rounded-2xl border border-stone-200/80 bg-white p-5 text-left shadow-sm transition-colors duration-300 hover:border-[#C5A059]">
                  <span className="rounded-xl bg-[#C5A059]/15 p-3 text-[#8a6b2e] transition-transform duration-300 group-hover:-rotate-6">
                    <FileText className="h-6 w-6" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C5A059]">{d.category}</span>
                    <span className="mt-1 block font-display text-lg font-semibold text-stone-900">{d.title}</span>
                    {d.description && <span className="mt-1 block text-sm leading-relaxed text-stone-500">{d.description}</span>}
                    <span className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#8a6b2e]">
                      Lire le document <span className="text-stone-300">•</span> <span className="font-normal text-stone-400">PDF, {formatBytes(d.size)}</span>
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {open && <PdfViewer doc={open} url={fileUrl(open.path)} onClose={() => setOpen(null)} />}
    </div>
  );
}
